function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import ViewFilterQueryContext from '../Context/ViewFilterQueryContext';

/**
 * Custom hook to handle view filter queries.
 * It must be used within a ViewFilterQueryProvider.
 *
 * @throws {Error} If the hook is not used within a ViewFilterQueryProvider.
 *
 * @typedef {Object} useViewFilterQueryReturn
 * @property {Object} filters - The current filters
 * @property {number} page - The current page number, adjusted because the API is 0-indexed but visually (in the query params) we want to start at 1
 * @property {Function} setCurrentPage - Function to set the current page. The page number is enforced to be at least 1.
 * @property {Function} setFilters - Function to set the filters. The page number is reset to 1 when filters change because the user is likely looking for a different set of results.
 * @property {Function} setFilterValue - Function to set a specific filter value
 *
 * @returns {useViewFilterQueryReturn} The return object.
 */
export default function useViewFilterQuery() {
  var context = React.useContext(ViewFilterQueryContext);
  if (!context) {
    throw new Error('useViewFilterQuery must be used within a ViewFilterQueryProvider');
  }
  var queryParams = context.queryParams,
    setQueryParams = context.setQueryParams,
    pageQueryParam = context.pageQueryParam;
  var _queryParams$pageQuer = queryParams[pageQueryParam],
    page = _queryParams$pageQuer === void 0 ? 1 : _queryParams$pageQuer,
    filters = _objectWithoutProperties(queryParams, [pageQueryParam].map(_toPropertyKey));
  var setCurrentPage = React.useCallback(function (setPageFn) {
    var newPage = page;

    // We don't want to allow pages < 0, so we enforce a minimum of 1
    if (typeof setPageFn !== 'function') {
      newPage = Math.max(1, parseInt(setPageFn));
    } else {
      newPage = Math.max(1, parseInt(setPageFn(page)));
    }
    setQueryParams(function (oldQueryParams) {
      return _objectSpread(_objectSpread({}, oldQueryParams), {}, _defineProperty({}, pageQueryParam, newPage));
    });
  }, [page, pageQueryParam, setQueryParams]);
  var setFilters = React.useCallback(function (setFiltersFn) {
    var newFilters = {};
    if (typeof setFiltersFn !== 'function') {
      newFilters = setFiltersFn;
    } else {
      newFilters = setFiltersFn(filters);
    }
    setQueryParams(function () {
      if (pageQueryParam) {
        return _objectSpread(_objectSpread({}, newFilters), {}, _defineProperty({}, pageQueryParam, 1));
      }
      return newFilters;
    });
  }, [filters, pageQueryParam, setQueryParams]);
  var setFilterValue = React.useCallback(function (filterName, filterValue) {
    setFilters(function (oldFilters) {
      return _objectSpread(_objectSpread({}, oldFilters), {}, _defineProperty({}, filterName, filterValue));
    });
  }, [setFilters]);
  return {
    filters: filters,
    // we subtract 1 from the page because the API is 0-indexed
    // but visually (in the query params) we want to start at 1
    page: Math.max(0, parseInt(page)) - 1,
    setCurrentPage: setCurrentPage,
    setFilters: setFilters,
    setFilterValue: setFilterValue
  };
}